/**
 * The numeric edges of a management policy, in one zod-free place.
 *
 * Both halves of M14 need these and neither may own them. `schemas/policy.ts` turns them
 * into zod constraints; `policy/compile.ts` checks them by hand when it reads a stored rule
 * set back into a policy, because it is deliberately zod-free so the web can import it
 * without pulling zod into the bundle.
 *
 * If those two ever disagreed, `inferPolicy` would hand the preset card an object that the
 * card's own schema rejects — a screen that refuses to save what it just loaded. The rule
 * schema is intentionally wider than the policy schema in two places (`MOVE_SL_TO_R`'s
 * rMultiple and `PARTIAL_CLOSE`'s pct), so an Advanced rule set can legitimately hold a
 * value no preset can express; that is a reason to fall back to Advanced mode, not to
 * widen these.
 */
export const POLICY_BOUNDS = {
  beOffsetPips: { min: -1000, max: 1000 },
  bufferSpreads: { min: 0, max: 20 },
  /** −1 leaves the original stop, 0 is breakeven, +0.5 banks half the risk. */
  stopAtR: { min: -1, max: 1 },
  /** Never 100: closing the whole thing is CLOSE_REMAINING, which is a different decision. */
  partialPct: { min: 1, max: 90 },
  /** `min` is exclusive — a zero-distance trail is not a trail. */
  trailingDistancePips: { min: 0, max: 100_000 },
} as const;

/**
 * The defaults, likewise shared. `inferPolicy` fills every parameter its preset does not
 * read with the value from here, and the zod schema defaults to the same, so a rule set
 * compiled from a default policy reads back as that same default policy.
 */
export const POLICY_DEFAULTS = {
  preset: "ladder",
  beOffsetPips: 0,
  bufferSpreads: 2,
  stopAtR: -0.5,
  trailToTp1AfterTp2: false,
  partialEnabled: false,
  partialAt: "TP1_HIT",
  partialPct: 50,
  trailingEnabled: false,
  trailingAt: "TP1_HIT",
  trailingDistancePips: 20,
  cancelPendingOnSignalClose: true,
  closeRemainingOnSignalClose: false,
} as const;
